<template>

  <div class="main" id="egcf-main">

    <loading-spinner v-if="isLoading"></loading-spinner>
    <stand-dialog v-if="$haveMaps" v-bind:on-close="onStandDialogClose" ref="standDialog"></stand-dialog>

    <product-detail-dialog v-if="productContainer" v-bind:open-stand-dialog="openStandDialog"
                           v-bind:themes="productContainer.themes" v-bind:on-close-dialog="onProductDialogClose"
                           ref="productDetailDialog"></product-detail-dialog>

    <exhibitor-detail-dialog v-if="exhibitorContainer" v-bind:open-stand-dialog="openStandDialog"
                             v-bind:themes="exhibitorContainer.themes"
                             ref="exhibitorDetailDialog"
                             v-bind:open-product-dialog="openProductDetailDialog"></exhibitor-detail-dialog>
    <template v-if="!isLoading">

      <div class="filter-search-position-container">
        <div class="filter-button-search-wrapper">
          <div class="search-input-container">
            <input type="search" v-model="searchValue" v-on:input="onSearchValueInput" spellcheck="false"
                   autocapitalize="none" v-bind:placeholder="$t('search-input-placeholder')"/>
          </div>
          <template v-if="exhibitorFilter.filterExists()">
            <div v-if="!hasActiveFilter()" class="active-filter-button active-filter-button-deactivated"
                 v-on:click="toggleFilter">
              FILTER
            </div>
            <div v-else class="active-filter-button active-filter-button-active" v-on:click="toggleFilter">
            <span class="active-filter-count-bubble">
            {{ Object.entries(activeFilters).length }}
          </span>
              FILTER
            </div>
          </template>
          <div class="filter-container" v-if="isFilterActive">
            <div class="filter" v-for="filter of exhibitorFilter.filters.ROOT">

              <div class="select">
                <select class="select-text" required v-model="activeFilters[filter.id]" v-on:change="onFilterChanged">
                  <option></option>
                  <option v-bind:value="option.id" v-for="option of exhibitorFilter.filters[filter.id]">
                    {{ option.title }}
                  </option>
                </select>
                <span class="select-bar"></span>
                <label class="select-label">{{ filter.title }}</label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="letter-filter-position-container">
        <div class="letter-filter-wrapper">
          <div class="letter">
            <div v-bind:class="activeLetterFilter === 'all' ? 'selected': ''" v-on:click="onLetterClicked('all')">
              {{ $t('all') }}
            </div>
          </div>
          <div class="letter" v-for="letter of exhibitorContainer.startLetters">
            <div
              :class="[{ 'selected': activeLetterFilter === letter},{'disabled': !exhibitorContainer.letterExistsInCurrentList(letter)}]"
              v-on:click="exhibitorContainer.letterExistsInCurrentList(letter) ? onLetterClicked(letter): () => {}">
              {{ letter }}
            </div>
          </div>
        </div>
      </div>


      <div class="exhibitor-list" ref="exhibitorList">
        <div class="category" v-if="exhibitorContainer && exhibitorContainer.hasEntries()"
             v-for="(exhibitorList, index)  of exhibitorContainer.getExhibitorsMap(pageSize)">

          <div class="category-header">{{ index }}</div>
          <div class="exhibitor" v-on:click="openExhibitorDetailDialog(exhibitor,$event)"
               v-for="exhibitor of exhibitorList"
               v-bind:id="exhibitor.vueId">
            <div class="exhibitor-content">
              <div :class="exhibitor.logo ? 'exhibitor-logo' : 'exhibitor-logo-letter'">
                <lazy-load-container ref="lazyLoadingContainer">
                  <img v-if="exhibitor.logo"
                       :src="exhibitor.getLogoUrl()">
                  <span v-else>{{ exhibitor.getFirstLetter() }}</span>
                </lazy-load-container>
              </div>
              <div class="exhibitor-infos">
                <span class="exhibitor-name">{{ exhibitor.name }}</span>
                <span class="exhibitor-location" v-if="exhibitor.location">{{ exhibitor.location }}</span>
                <span class="exhibitor-description" v-if="exhibitor.info"
                      v-html="truncateExhibitorInfo(exhibitor.info)"></span>
                <div class="stand-container" v-if="exhibitor.stands">
                  <i class="fas fa-map-marker-alt"></i>
                  <div class="stands">
                    <div class="stand" v-for="(stand,index) of exhibitor.stands"
                         v-on:click="$haveMaps ? openStandDialog(stand,$event):openExhibitorDetailDialog(exhibitor,$event)"
                         v-html="(stand.getDisplayName() + (index !== exhibitor.stands.length -1 ? ',' : ''))">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <span class="empty-search-results" v-else-if="exhibitorContainer && !exhibitorContainer.hasEntries()">
           {{ $t('empty-search-results') }}
        </span>
      </div>
      <div ref="scrollToTopButton" class="scroll-to-top-button" v-on:click="scrollTop">
        <svg xmlns="http://www.w3.org/2000/svg" fill="white"
             viewBox="0 0 448 512">
          <!-- Font Awesome Pro 5.15.4 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license (Commercial License) -->
          <path
            d="M34.9 289.5l-22.2-22.2c-9.4-9.4-9.4-24.6 0-33.9L207 39c9.4-9.4 24.6-9.4 33.9 0l194.3 194.3c9.4 9.4 9.4 24.6 0 33.9L413 289.4c-9.5 9.5-25 9.3-34.3-.4L264 168.6V456c0 13.3-10.7 24-24 24h-32c-13.3 0-24-10.7-24-24V168.6L69.2 289.1c-9.3 9.8-24.8 10-34.3.4z"/>
        </svg>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import {Options, Vue} from 'vue-class-component'
import {Services} from "@/Services";
import {ExhibitorFilter} from "@/entities/ExhibitorFilter";
import {ExhibitorContainer} from "@/entities/ExhibitorContainer";
import {isDefined, scrollToTop} from "@/utils";
import LazyLoadContainer from "@/components/LazyLoadComponent.vue";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import ExhibitorDetailDialog from "@/components/ExhibitorDetailDialog.vue";
import {Exhibitor} from "@/entities/Exhibitor";
import StandDialog from "@/components/StandDialog.vue";
import Stand from './entities/Stand';
import {zip} from 'rxjs';
import {Observable} from "rxjs/dist/types";
import {ProductContainer} from '@/entities/ProductContainer';
import {Product} from '@/entities/Product';
import ProductDetailDialog from '@/components/ProductDetailDialog.vue';

@Options({components: {ProductDetailDialog, LazyLoadContainer, LoadingSpinner, ExhibitorDetailDialog, StandDialog}})
export default class App extends Vue {

  private productContainer: ProductContainer = null;
  private exhibitorContainer: ExhibitorContainer = null;
  private exhibitorFilter: ExhibitorFilter = new ExhibitorFilter({});
  private searchValue: string = '';
  private pageSize = 20;
  private basePageSize = 20;
  private activeFilters: Record<string, string> = {};
  private isFilterActive: boolean = false;
  private isLoading: boolean = true;
  private activeLetterFilter: string = 'all';

  private selectedExhibitor: Exhibitor;

  constructor(props: any) {
    super(props);

    let loadObservables: Observable<any>[] = [Services.getExhibitorFilters(), Services.getExhibitors()]
    if (process.env.VUE_APP_ENABLE_PRODUCTS === 'true') {
      loadObservables.push(Services.getProducts())
    }

    zip(...loadObservables).subscribe({
      next: value => {
        this.exhibitorFilter = value[0];

        if (process.env.VUE_APP_ENABLE_PRODUCTS === 'true') {
          value[1].exhibitors.forEach(e => {
            let productsForExhibitor = value[2].products.filter(p => p.companyId === e.id);
            if (isDefined(productsForExhibitor) && productsForExhibitor.length > 0) {
              e.products = productsForExhibitor;
            }
          });
          this.productContainer = value[2];
        }
        this.exhibitorContainer = value[1];
        this.isLoading = false;
      },
      error: err => {
        Services.sendErrorMessage(err)
      }
    })
  }

  mounted() {
    window.addEventListener('scroll', this.handleScrollEvent);
  }

  public truncateExhibitorInfo(info: string): string {
    let newInfo = info;
    if (newInfo.includes('<p>') && newInfo.includes('</p>')) {
      const tagStart = newInfo.indexOf('<p>') + 3;
      const tagEnd = newInfo.indexOf('</p>', tagStart);
      if (tagStart > -1 && tagEnd > -1) {
        newInfo = newInfo.substr(tagStart, tagEnd - tagStart);
        return newInfo
      }
    }
    return info;
  }

  private handleScrollEvent(): void {
    if (isDefined(this.$refs.exhibitorList)) {
      //@ts-ignore
      let bottomOfWindow = document.documentElement.scrollTop + window.innerHeight >= this.$refs.exhibitorList.offsetHeight;
      if (bottomOfWindow) {
        this.loadNextPage()
      }
    }
    if (isDefined(this.$refs.scrollToTopButton)) {
      if (document.body.scrollTop > 200 || document.documentElement.scrollTop > 200) {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "block";
      } else {
        (this.$refs.scrollToTopButton as HTMLElement).style.display = "none";
      }
    }
  }

  private scrollTop(): void {
    scrollToTop();
  }

  private onSearchValueInput(event): void {
    if (isDefined(this.exhibitorContainer)) {
      this.resetPageSize();
      this.exhibitorContainer.filterExhibitors(this.searchValue, this.activeFilters, this.activeLetterFilter);
      this.detectChanges();
    }
  }

  private detectChanges(): void {
    this.$nextTick(() => {
      const lazyLoadingContainers = this.$refs.lazyLoadingContainer as LazyLoadContainer[];
      for (const c of lazyLoadingContainers) {
        c.detectChanges();
      }
    });
  }

  private loadNextPage(): void {
    this.pageSize += this.basePageSize;
  }

  private onFilterChanged(event?): void {
    for (const [key, value] of Object.entries(this.activeFilters)) {
      if (value.length === 0) {
        delete this.activeFilters[key];
      }
    }
    if (isDefined(this.exhibitorContainer)) {
      this.resetPageSize();
      this.exhibitorContainer.filterExhibitors(this.searchValue, this.activeFilters, this.activeLetterFilter)
      this.detectChanges();
    }
  }

  private resetPageSize(): void {
    this.pageSize = this.basePageSize;
  }

  private toggleFilter(): void {
    this.isFilterActive = !this.isFilterActive;
    if (!this.isFilterActive) {
      this.resetFilter();
    }
  }

  private resetFilter(): void {
    this.activeFilters = {};
    this.onFilterChanged();
  }

  private hasActiveFilter(): boolean {
    return Object.entries(this.activeFilters).length > 0;
  }

  private openExhibitorDetailDialog(exhibitor: Exhibitor, event: Event): void {
    event.stopImmediatePropagation();
    this.selectedExhibitor = exhibitor;
    const detailDialog: ExhibitorDetailDialog = this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.openDialog(exhibitor);
    }
  }

  private closeExhibitorDetailDialog(event: Event): void {
    event.stopImmediatePropagation();
    const detailDialog: ExhibitorDetailDialog = this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.closeDialog();
    }
  }

  private openStandDialog(stand: Stand, event: Event): void {
    event.stopImmediatePropagation();
    if (isDefined(this.$refs.standDialog)) {
      (this.$refs.standDialog as StandDialog).openDialog(stand);
    }
  }

  private onStandDialogClose(): void {
    const detailDialog: ExhibitorDetailDialog = this.$refs.exhibitorDetailDialog as ExhibitorDetailDialog;
    if (isDefined(detailDialog) && detailDialog.isOpen) {
      detailDialog.addClickListener();
    }
  }

  private onProductDialogClose(event: Event): void {
    this.openExhibitorDetailDialog(this.selectedExhibitor, event)
  }

  private openProductDetailDialog(product: Product, event: Event): void {
    if (event) {
      event.stopImmediatePropagation();
    }
    this.closeExhibitorDetailDialog(event);
    const detailDialog: ProductDetailDialog = this.$refs.productDetailDialog as ProductDetailDialog;
    if (isDefined(detailDialog)) {
      detailDialog.openDialog(product);
    }
  }


  private onLetterClicked(letter: string): void {
    this.activeLetterFilter = letter;
    this.onFilterChanged();
  }

}
</script>

<style lang="scss">
@import "assets/scss/main";
</style>

<style lang="css">
@import "./assets/css/all.css";
</style>

